import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LOCATIONS } from '../../utils/constants';
import { createLocation, updateLocation, deleteLocation, fetchLocations } from '../../store/slices/locationsSlice';

const LocationList = ({ isEditing }) => {
  const dispatch = useDispatch();
  const { locations, loading } = useSelector((state) => state.locations);
  
  // Fetch locations when component mounts
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Get fallback locations from constants.js if database is empty
  const getFallbackLocations = () => {
    return LOCATIONS.map(name => ({ name, isActive: true, id: `fallback-${name}` }));
  };

  // Filter active locations
  // If database is empty, use fallback from constants.js
  let filteredLocations = [];
  if (locations.length === 0) {
    // Use fallback from constants.js if database is empty
    filteredLocations = getFallbackLocations();
  } else {
    filteredLocations = locations.filter((location) => location.isActive !== false);
  }

  const handleAddLocation = async () => {
    if (!newLocation.name.trim()) {
      alert('Please enter a location name');
      return;
    }

    try {
      await dispatch(createLocation({
        name: newLocation.name.trim(),
        isActive: true,
      }));
      await dispatch(fetchLocations()); // Refresh list
      setNewLocation({ name: '' });
      setShowAddForm(false);
    } catch (error) {
      alert('Error adding location: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateLocation = async (locationId, field, value) => {
    try {
      const location = locations.find((l) => l.id === locationId || l._id === locationId);
      if (!location) return;

      const updatedData = { ...location };
      if (field === 'name') {
        updatedData.name = value;
      }

      await dispatch(updateLocation({ id: locationId, data: updatedData }));
      await dispatch(fetchLocations()); // Refresh list
      setEditingId(null);
    } catch (error) {
      alert('Error updating location: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await dispatch(deleteLocation(locationId));
      await dispatch(fetchLocations()); // Refresh list
      setDeleteConfirm(null);
    } catch (error) {
      alert('Error deleting location: ' + (error.message || 'Unknown error'));
    }
  };

  const startEdit = (location) => {
    // If it's a fallback location (from constants.js), we need to create it in the database first
    if (location.id && location.id.startsWith('fallback-')) {
      // Create the location in the database
      handleAddLocationFromFallback(location);
      return;
    }
    setEditingId(location.id || location._id);
    setNewLocation({
      name: location.name,
    });
    setShowAddForm(true);
  };

  const handleAddLocationFromFallback = async (location) => {
    try {
      await dispatch(createLocation({
        name: location.name,
        isActive: true,
      }));
      await dispatch(fetchLocations()); // Refresh list
      // Now start editing the newly created location
      setTimeout(() => {
        dispatch(fetchLocations()).then(() => {
          const updatedLocations = locations.filter(l => l.name === location.name);
          if (updatedLocations.length > 0) {
            startEdit(updatedLocations[0]);
          }
        });
      }, 500);
    } catch (error) {
      alert('Error creating location: ' + (error.message || 'Unknown error'));
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setNewLocation({ name: '' });
  };

  if (loading && locations.length === 0) {
    return <div className="text-sm text-gray-500 py-4 text-center">Loading locations...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Locations ({filteredLocations.length})
        </h3>
        {isEditing && (
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingId(null);
              setNewLocation({ name: '' });
            }}
            className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            +
          </button>
        )}
      </div>

      {/* Add/Edit Location Form */}
      {showAddForm && isEditing && (
        <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
          <input
            type="text"
            value={newLocation.name}
            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
            placeholder="Location name"
            className="w-full mb-2 border border-gray-300 rounded px-2 py-1 text-sm"
            disabled={!!editingId}
          />
          <div className="flex gap-2">
            <button
              onClick={editingId ? () => handleUpdateLocation(editingId, 'name', newLocation.name) : handleAddLocation}
              className="flex-1 bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors"
            >
              {editingId ? 'Update' : 'Add'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Location List */}
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {filteredLocations.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No locations added yet</p>
        ) : (
          filteredLocations.map((location) => {
            const locationId = location.id || location._id;
            const isCurrentlyEditing = editingId === locationId;

            return (
              <div
                key={locationId}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{location.name}</p>
                </div>
                {isEditing && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(location)}
                      className="p-1 text-gray-600 hover:text-orange-600 transition-colors"
                      aria-label="Edit"
                      title={location.id && location.id.startsWith('fallback-') ? 'Add to database to edit' : 'Edit location'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {!(location.id && location.id.startsWith('fallback-')) && (
                      <button
                        onClick={() => setDeleteConfirm({ id: locationId, name: location.name })}
                        className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                        aria-label="Delete"
                        title="Delete location"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Location?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will set it as inactive.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleDeleteLocation(deleteConfirm.id)}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationList;

